import React, { useState, useMemo, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Chip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    InputAdornment,
    TablePagination,
    CircularProgress,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import ElevatedButton from "components/Button/ElevatedButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import { colors } from "components/Theme/WebTheme";

const FileView = ({ files, handleFileClick, getFileIcon }) => {
    console.log(
        "🔍 FileView rendered with files:",
        files,
        "type:",
        typeof files,
        "isArray:",
        Array.isArray(files)
    );

    if (!files) {
        console.log("🔍 No files to display");
        return null;
    }

    // Handle both array and object formats, with better error handling
    let fileArray = [];
    try {
        if (Array.isArray(files)) {
            fileArray = files;
        } else if (typeof files === "object" && files !== null) {
            fileArray = Object.values(files);
        } else {
            console.log("🔍 Unknown files format:", typeof files, files);
            return (
                <Typography variant="caption" color="error">
                    Invalid file format
                </Typography>
            );
        }
    } catch (error) {
        console.error("🔍 Error processing files:", error);
        return (
            <Typography variant="caption" color="error">
                Error loading files
            </Typography>
        );
    }

    console.log(
        "🔍 FileView processed array:",
        fileArray,
        "length:",
        fileArray.length
    );

    if (fileArray.length === 0) {
        return (
            <Typography variant="caption" color="textSecondary">
                No files available
            </Typography>
        );
    }

    return fileArray.map((file, index) => {
        if (!file) {
            console.log("🔍 Skipping null/undefined file at index:", index);
            return null;
        }

        return (
            <Box
                key={file.id || `file-${index}`}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "8px",
                    cursor: file.url ? "pointer" : "default",
                    "&:hover": {
                        backgroundColor: file.url ? "#f5f5f5" : "transparent",
                    },
                }}
                onClick={() => file.url && handleFileClick(file)}
            >
                {getFileIcon(file.type)}
                <Box>
                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ ml: 1 }}
                    >
                        {file.name}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        sx={{
                            ml: 1,
                            fontSize: 12,
                        }}
                    >
                        {file.size} MB
                    </Typography>
                </Box>
            </Box>
        );
    });
};

const DocumentUserTable = ({ documentUsers, onReject, onApprove }) => {
    const { t } = useTranslation();
    const [selectedFile, setSelectedFile] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [openDetailDialog, setOpenDetailDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const [loadingDocuments, setLoadingDocuments] = useState({}); // Track loading state per document ID

    // Get users data and loading state from Redux store
    const usersData = useSelector((state) => {
        const usersdata = state.usersdata;
        return usersdata?.users || usersdata?.staticusers || [];
    });
    const usersLoading = useSelector((state) => state.usersdata?.loading || false);
    const documentUsersLoading = useSelector((state) => state.documentUsersData?.loading || false);
    
    // Check if data is loading (only show loading if actually loading, not if data is empty)
    const isLoading = usersLoading || documentUsersLoading;

    // Create mapping from user_id to usertype
    const userTypeMap = useMemo(() => {
        const map = {};
        const users = Array.isArray(usersData) ? usersData : [];
        
        if (users.length > 0) {
            users.forEach((user) => {
                // Map using both uid and id (some users might have one or the other)
                const userId = user.uid || user.id;
                if (userId) {
                    const userType = user.usertype || user.user_type || null;
                    map[userId] = userType;
                    // Also map by id if different from uid
                    if (user.id && user.id !== userId) {
                        map[user.id] = userType;
                    }
                }
            });
            console.log("🔍 userTypeMap created with", Object.keys(map).length, "entries:", map);
            if (users.length > 0) {
                console.log("🔍 Sample users data:", users.slice(0, 3).map(u => ({
                    uid: u.uid,
                    id: u.id,
                    usertype: u.usertype,
                    firstName: u.firstName,
                    lastName: u.lastName
                })));
            }
        } else {
            console.log("⚠️ No users data available. usersData:", usersData);
        }
        return map;
    }, [usersData]);

    // Group documents by user_id or user_email
    const groupedByUser = useMemo(() => {
        const grouped = {};
        documentUsers.forEach((doc) => {
            // Skip documents that don't have user_id or user_id not found in users collection
            if (!doc.user_id || !(doc.user_id in userTypeMap)) {
                if (doc.user_id) {
                    console.log(`⚠️ Skipping document for user ${doc.user_id} - user not found in users collection`);
                }
                return;
            }

            const userKey = doc.user_id || doc.user_email;
            if (!grouped[userKey]) {
                // Get user_type from users collection (should always exist since we filtered)
                const userType = userTypeMap[doc.user_id] || null;
                
                if (doc.user_id) {
                    console.log(`🔍 Grouping user ${doc.user_id}:`, {
                        user_id: doc.user_id,
                        userTypeFromMap: userTypeMap[doc.user_id],
                        finalUserType: userType
                    });
                }
                
                grouped[userKey] = {
                    user_id: doc.user_id,
                    user_email: doc.user_email,
                    user_name: doc.user_name,
                    user_type: userType,
                    documents: [],
                };
            }
            grouped[userKey].documents.push(doc);
            // Always update user_type from users collection if available (override doc.user_type)
            if (doc.user_id && userTypeMap[doc.user_id] !== undefined) {
                grouped[userKey].user_type = userTypeMap[doc.user_id];
            }
        });
        const result = Object.values(grouped);
        console.log("🔍 Grouped users result (filtered, first 3):", result.slice(0, 3).map(u => ({
            user_id: u.user_id,
            user_name: u.user_name,
            user_type: u.user_type,
            documents_count: u.documents.length
        })));
        console.log(`🔍 Total grouped users: ${result.length} (filtered from ${documentUsers.length} documents)`);
        return result;
    }, [documentUsers, userTypeMap]);

    // Filter grouped users based on search query (search by user name, email, or document name)
    const filteredUsers = useMemo(() => {
        if (!searchQuery) return groupedByUser;
        
        const query = searchQuery.toLowerCase();
        return groupedByUser.filter((userGroup) => {
            // Check user name or email
            if (
                userGroup.user_name?.toLowerCase().includes(query) ||
                userGroup.user_email?.toLowerCase().includes(query)
            ) {
                return true;
            }
            // Check if any document matches
            return userGroup.documents.some((doc) =>
                doc.field?.name?.toLowerCase().includes(query) ||
                doc.field?.description?.toLowerCase().includes(query) ||
                doc.id?.toLowerCase().includes(query)
            );
        });
    }, [groupedByUser, searchQuery]);

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Get paginated users
    const paginatedUsers = filteredUsers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Update selectedDocument when documentUsers changes (to reflect Redux updates)
    useEffect(() => {
        if (selectedDocument && documentUsers.length > 0) {
            // Find the updated user group from documentUsers
            const updatedUserGroup = groupedByUser.find(
                (ug) => ug.user_id === selectedDocument.user_id || ug.user_email === selectedDocument.user_email
            );
            
            if (updatedUserGroup) {
                setSelectedDocument(updatedUserGroup);
            }
        }
    }, [documentUsers]);

    // Auto-clear loading state when document status changes
    // Check documentUsers directly for status changes
    useEffect(() => {
        // Check all documents in documentUsers for status changes
        documentUsers.forEach((doc) => {
            if (loadingDocuments[doc.id]?.loading) {
                // Check if document status has changed (approved/rejected)
                if (doc.status === "approved" || doc.status === "rejected") {
                    setLoadingDocuments((prev) => {
                        const newState = { ...prev };
                        delete newState[doc.id];
                        return newState;
                    });
                }
            }
        });
    }, [documentUsers, loadingDocuments]);

    const getFileIcon = (type) => {
        if (type === "pdf") {
            return <PictureAsPdfIcon color="error" />;
        }
        return <ImageIcon color="primary" />;
    };

    const handleFileClick = (file) => {
        setSelectedFile(file);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedFile(null);
    };

    const handleOpenDetailDialog = (userGroup) => {
        setSelectedDocument(userGroup);
        setOpenDetailDialog(true);
    };

    const handleCloseDetailDialog = () => {
        setOpenDetailDialog(false);
        setSelectedDocument(null);
    };

    const handleApprove = (user) => {
        console.log("🔍 DocumentUserTable handleApprove called with user:", {
            id: user.id,
            files: user.files,
            new_files: user.new_files,
            files_type: typeof user.files,
            new_files_type: typeof user.new_files,
        });

        // Set loading state for this document
        setLoadingDocuments((prev) => ({
            ...prev,
            [user.id]: { type: "approve", loading: true },
        }));

        // Pass the original user data to the parent component
        // Let the parent component handle the approval logic and Cloud Function call
        // Loading will be cleared automatically when document status changes via useEffect
        onApprove(user).catch((error) => {
            console.error("Error in handleApprove:", error);
            // Clear loading on error
            setLoadingDocuments((prev) => {
                const newState = { ...prev };
                delete newState[user.id];
                return newState;
            });
        });
    };

    const handleReject = (user) => {
        const isRemove = user.files === null || user.files?.length === 0;

        // Set loading state for this document
        setLoadingDocuments((prev) => ({
            ...prev,
            [user.id]: { type: "reject", loading: true },
        }));

        const updatedField = {
            ...user,
            approved: false,
            files: null,
            new_files: null,
        };
        
        // Loading will be cleared automatically when document status changes via useEffect
        onReject(updatedField, isRemove).catch((error) => {
            console.error("Error in handleReject:", error);
            // Clear loading on error
            setLoadingDocuments((prev) => {
                const newState = { ...prev };
                delete newState[user.id];
                return newState;
            });
        });
    };

    return (
        <Box>
            <Box sx={{ mb: 3, mt: 8 }}>
                <Typography variant="h5" component="h1">
                    {t("data_collected")}
                </Typography>
            </Box>

            {/* Add search bar below title */}
            <TextField
                fullWidth
                variant="outlined"
                placeholder={t("search_by_field_name") || "Buscar por nombre, email, ID o tipo de documento..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                    mb: 2,
                    backgroundColor: colors.WHITE,
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: colors.MAIN_COLOR }}>
                            <TableCell sx={styles.alignTop}>
                                {t("user") || "Usuario"}
                            </TableCell>
                            <TableCell sx={styles.alignTop}>
                                {t("type") || "Tipo"}
                            </TableCell>
                            <TableCell sx={styles.alignTop}>
                                {t("data") || "Datos"}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={3} sx={{ textAlign: "center", py: 8 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 2,
                                        }}
                                    >
                                        <CircularProgress size={40} sx={{ color: colors.MAIN_COLOR }} />
                                        <Typography variant="body2" color="textSecondary">
                                            {t("loading") || "Loading..."}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : paginatedUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} sx={{ textAlign: "center", py: 4 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {t("no_data") || "No data available"}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedUsers.map((userGroup) => {
                                return (
                                    <TableRow key={userGroup.user_id || userGroup.user_email}>
                                        <TableCell sx={styles.alignTop}>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                {userGroup.user_name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                display="block"
                                            >
                                                {userGroup.user_email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={styles.alignTop}>
                                            <Chip
                                                label={t(userGroup.user_type) || userGroup.user_type}
                                                size="small"
                                                color={
                                                    userGroup.user_type === "conductor" || userGroup.user_type === "driver"
                                                        ? "success"
                                                        : "primary"
                                                }
                                            />
                                        </TableCell>
                                        <TableCell sx={styles.alignTop}>
                                            <ElevatedButton
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => handleOpenDetailDialog(userGroup)}
                                                sx={{ fontSize: "0.875rem" }}
                                            >
                                                {t("details") || "Details"}
                                            </ElevatedButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredUsers.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10]}
                />
            </TableContainer>

            {/* File Preview Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography variant="h6">
                            {selectedFile?.name}
                        </Typography>

                        <IconButton onClick={handleCloseDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{ p: 2, display: "flex", justifyContent: "center" }}
                    >
                        {selectedFile?.url ? (
                            selectedFile.type === "pdf" ? (
                                <iframe
                                    src={selectedFile.url}
                                    style={{
                                        width: "100%",
                                        height: "600px",
                                        border: "none",
                                    }}
                                    title={selectedFile.name}
                                />
                            ) : (
                                <img
                                    src={selectedFile.url}
                                    alt={selectedFile.name}
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "80vh",
                                        objectFit: "contain",
                                    }}
                                />
                            )
                        ) : (
                            "File preview not available"
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Document Detail Dialog */}
            <Dialog
                open={openDetailDialog}
                onClose={handleCloseDetailDialog}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography variant="h6">
                            {t("document_details") || "Detalles del Documento"}
                        </Typography>
                        <IconButton onClick={handleCloseDetailDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedDocument && (
                        <Box>
                            {/* User Info Section */}
                            <Box
                                sx={{
                                    p: 2,
                                    mb: 3,
                                    border: "2px solid",
                                    borderColor: "error.main",
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="body1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                                    {t("user") || "Usuario"}: {selectedDocument.user_name}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                    {t("type") || "Tipo"}: {t(selectedDocument.user_type) || selectedDocument.user_type}
                                </Typography>
                            </Box>

                            {/* Document Details Table */}
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: colors.MAIN_COLOR }}>
                                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                                                {t("document") || "Documento"}
                                            </TableCell>
                                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                                                {t("information") || "Información"}
                                            </TableCell>
                                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                                                {t("files") || "Archivos"}
                                            </TableCell>
                                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                                                {t("update_files") || "Actualizar Archivos"}
                                            </TableCell>
                                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                                                {t("state") || "Estado"}
                                            </TableCell>
                                            <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                                                {t("actions") || "Comportamiento"}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedDocument.documents && selectedDocument.documents.map((doc) => (
                                            <TableRow key={doc.id}>
                                                <TableCell sx={styles.alignTop}>
                                                    {doc.field?.name || "N/A"}
                                                </TableCell>
                                                <TableCell sx={styles.alignTop}>
                                                    {doc.field?.description || "-"}
                                                </TableCell>
                                                <TableCell sx={styles.alignTop}>
                                                    <FileView
                                                        files={doc.files}
                                                        handleFileClick={handleFileClick}
                                                        getFileIcon={getFileIcon}
                                                    />
                                                </TableCell>
                                                <TableCell sx={styles.alignTop}>
                                                    <FileView
                                                        files={doc.new_files}
                                                        handleFileClick={handleFileClick}
                                                        getFileIcon={getFileIcon}
                                                    />
                                                </TableCell>
                                                <TableCell sx={styles.alignTop}>
                                                    <Chip
                                                        label={
                                                            doc.status === "rejected"
                                                                ? t("rejected") || "Rechazado"
                                                                : doc.status === "approved"
                                                                ? t("approved") || "Aprobado"
                                                                : t("pending") || "Pendiente"
                                                        }
                                                        color={
                                                            doc.status === "rejected"
                                                                ? "error"
                                                                : doc.status === "approved"
                                                                ? "success"
                                                                : "warning"
                                                        }
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell sx={styles.alignTop}>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            gap: 1,
                                                            flexWrap: "wrap",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        {loadingDocuments[doc.id]?.loading ? (
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 1,
                                                                    minWidth: 140,
                                                                }}
                                                            >
                                                                <CircularProgress size={18} />
                                                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                                                                    {loadingDocuments[doc.id]?.type === "approve"
                                                                        ? t("approving") || "Approving..."
                                                                        : t("rejecting") || "Rejecting..."}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <>
                                                                <ElevatedButton
                                                                    variant="contained"
                                                                    color="error"
                                                                    size="small"
                                                                    disabled={
                                                                        doc.approved ||
                                                                        doc.status === "rejected" ||
                                                                        loadingDocuments[doc.id]?.loading
                                                                    }
                                                                    onClick={() => {
                                                                        handleReject(doc);
                                                                        // Modal will remain open - user can close manually via close button
                                                                    }}
                                                                    sx={{ fontSize: "0.875rem" }}
                                                                >
                                                                    {t("reject") || "RECHAZAR"}
                                                                </ElevatedButton>
                                                                <ElevatedButton
                                                                    variant="contained"
                                                                    color="primary"
                                                                    size="small"
                                                                    disabled={
                                                                        doc.approved ||
                                                                        doc.status === "rejected" ||
                                                                        loadingDocuments[doc.id]?.loading
                                                                    }
                                                                    onClick={() => {
                                                                        handleApprove(doc);
                                                                        // Modal will remain open - user can close manually via close button
                                                                    }}
                                                                    sx={{ fontSize: "0.875rem" }}
                                                                >
                                                                    {t("approve") || "APROBAR"}
                                                                </ElevatedButton>
                                                            </>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Close Button */}
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                                <ElevatedButton
                                    variant="outlined"
                                    onClick={handleCloseDetailDialog}
                                    sx={{ fontSize: "0.875rem" }}
                                >
                                    {t("close") || "Cerrar"}
                                </ElevatedButton>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default DocumentUserTable;

const styles = {
    alignTop: {
        verticalAlign: "top",
    },
};
